import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Img } from "@/components/Img";
import { EASE } from "@/components/Motion";

const Layer = ({ z, className, delay, children }) => (
  <motion.div
    className={`absolute ${className}`}
    style={{ transform: `translateZ(${z}px)` }}
    initial={{ opacity: 0, clipPath: "inset(100% 0% 0% 0%)" }}
    animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
    transition={{ duration: 1.2, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const SWATCHES = ["#142B4A", "#2457C5", "#C8FF3D", "#F8F7F4"];

export const HeroComposition = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 110, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onPointerMove={onMove} onPointerLeave={reset} className="relative h-full min-h-[460px] sm:min-h-[620px]" style={{ perspective: 1400 }} data-testid="hero-composition">
      <motion.div className="absolute inset-5 sm:inset-10 lg:inset-12" style={{ rotateX, rotateY, y, transformStyle: "preserve-3d" }}>
        <Layer z={30} delay={0.35} className="left-[16%] top-[6%] w-[58%] shadow-[0_40px_80px_-30px_rgba(16,33,58,0.7)]">
          <Img name="identity" eager alt="Concept brand identity stationery in navy and royal blue" sizes="(min-width:1024px) 22vw, 55vw" className="aspect-[3/4] w-full rounded-md object-cover" />
        </Layer>
        <Layer z={70} delay={0.5} className="right-0 top-0 w-[46%] shadow-[0_30px_60px_-25px_rgba(16,33,58,0.6)]">
          <Img name="website" eager alt="Concept website landing page interface on a laptop" sizes="(min-width:1024px) 18vw, 45vw" className="aspect-[4/3] w-full rounded-md object-cover" />
        </Layer>
        <Layer z={110} delay={0.65} className="bottom-[2%] left-0 w-[38%] shadow-[0_30px_60px_-25px_rgba(16,33,58,0.7)]">
          <Img name="packaging" eager alt="Concept premium navy packaging range with lime accents" sizes="(min-width:1024px) 15vw, 38vw" className="aspect-[3/4] w-full rounded-md object-cover" />
        </Layer>
        <Layer z={140} delay={0.8} className="bottom-[8%] right-[2%] w-[42%]">
          <div className="on-dark rounded-md bg-navy p-4 text-white shadow-[0_30px_60px_-20px_rgba(16,33,58,0.8)] sm:p-6">
            <div className="flex items-start justify-between">
              <span className="font-display text-5xl font-extrabold leading-none tracking-[-0.06em] sm:text-7xl">Aa</span>
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/70 sm:text-[0.65rem]">Type study</span>
            </div>
            <p className="mt-3 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-white/70 sm:text-[0.65rem]">Manrope · 800 / 400</p>
            <div className="mt-4 flex gap-1.5">
              {SWATCHES.map((c) => <span key={c} className="h-6 flex-1 rounded-sm ring-1 ring-white/20 sm:h-8" style={{ background: c }} />)}
            </div>
          </div>
        </Layer>
        <Layer z={180} delay={1} className="left-[4%] top-[2%]">
          <span className="inline-flex items-center gap-2 rounded-full bg-lime px-3.5 py-1.5 font-mono text-[0.62rem] font-medium uppercase tracking-[0.16em] text-navy shadow-lg">
            <span className="h-1.5 w-1.5 rounded-full bg-navy" aria-hidden="true" /> Concept work
          </span>
        </Layer>
      </motion.div>
    </div>
  );
};
